import React, { useMemo } from 'react';

import { useAuth as useSalesWebAuth } from '@auth-wrapper';
import type { UserInfo as SalesWebUserInfo, UserOrganization } from '@contexts/users/types';

export interface AuthenticationState {
  isAuthenticated: boolean;
  accessToken?: string;
}

export interface IAuthContext {
  authStatus: AuthenticationState;
  setIsPrivateRoute: React.Dispatch<React.SetStateAction<boolean>>;
  user?: UserInfo;
  logout: () => void;
  loading: boolean;
  token?: string;
}

export interface UserInfo {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  groups: Array<UserGroup>;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  isSupplier?: boolean;
  isCustomer?: boolean;
  roles?: ('admin' | 'superadmin' | 'supplier' | 'customer')[];
  permissions?: string[];
  pictureUrl?: string;
  createdAt?: string;
  organizations: UserOrganization[];
}

export type UserGroup =
  | 'Admin Customer'
  | 'Admin Especial de Customer'
  | 'Admin Especial do Marketplace'
  | 'Admin Especial de Vendas'
  | 'Customer User'
  | 'Marketplace Admin'
  | 'Marketplace Cliente'
  | 'Marketplace Fornecedor'
  | 'Relatorio Economia Admin'
  | 'Vendas Admin'
  | 'Vendas Vendedor'
  | 'Vendas Revendedor'
  | 'Vendas White Label';

export const toSellerAreaUser = (user?: SalesWebUserInfo): UserInfo | undefined => {
  if (!user) return undefined;
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    phone: user.phone,
    pictureUrl: user.pictureUrl,
    organizations: user.organizations ?? [],
    isAdmin: false,
    isSuperAdmin: false,
    isSupplier: false,
    isCustomer: false,
    roles: [],
    groups: [],
    permissions: [],
  };
};

export const useAuth = (): IAuthContext => {
  const auth = useSalesWebAuth();
  const user = useMemo(() => toSellerAreaUser(auth.user), [auth.user]);
  return {
    authStatus: auth.authStatus,
    setIsPrivateRoute: auth.setIsPrivateRoute,
    logout: auth.logout,
    loading: auth.loading,
    token: auth.token,
    user,
  };
};
